/**
 * parcoursProgressionStorage.js
 *
 * Stockage de la progression parcours-specifique.
 * Gere la progression qui ne peut PAS etre derivee des autres storages
 * (quiz, drill, memorisation en tant qu'etapes parcours).
 *
 * Les etapes derivables (discovery, rhythm-group) sont evaluees
 * directement depuis discoveryStorage et progressStorage.
 *
 * localStorage primary + Firestore sync (parcoursProgression/{userId}).
 */

import { getStorageKey, getCurrentUserId } from './userStorage';
import { db } from '../firebaseConfig';
import { doc, getDoc, setDoc, updateDoc, deleteField } from 'firebase/firestore';
import { emitProgressionChanged } from './parcoursEvents';
import { loadParcoursConfig, getChapitres } from './parcoursConfigLoader';
import RouletteProgressService from '../services/RouletteProgressService';
import { markTierParcoursUnlocked } from './rouletteStorage';
import MonitoringService from '../services/MonitoringService';
import { findChapitreOvershoot, isValidChapitreAdvance, startChapitreFromDrumYears, dropChapitresAfter } from './parcoursProgressionGuard';

const BASE_KEY = 'parcours_progression';

// ============================================================
// Helpers localStorage
// ============================================================

function migratePhaseKeysToChapitre(progression) {
  if (!progression || progression.currentChapitre !== undefined) return progression;
  const migrated = {};
  if (progression.currentPhase !== undefined) {
    migrated.currentChapitre = progression.currentPhase;
  }
  for (const key of Object.keys(progression)) {
    if (/^phase\d+$/.test(key)) {
      migrated[key.replace('phase', 'chapitre')] = progression[key];
    } else if (key !== 'currentPhase') {
      migrated[key] = progression[key];
    }
  }
  return migrated;
}

function loadFromLocalStorage() {
  try {
    const key = getStorageKey(BASE_KEY);
    const data = localStorage.getItem(key);
    if (!data) return getDefaultProgression();
    const parsed = JSON.parse(data);
    const migrated = migratePhaseKeysToChapitre(parsed);
    if (migrated !== parsed) saveToLocalStorage(migrated);
    return migrated;
  } catch (e) {
    console.error('[ParcoursProgressionStorage] Erreur lecture localStorage:', e);
    return getDefaultProgression();
  }
}

function saveToLocalStorage(progression) {
  try {
    const key = getStorageKey(BASE_KEY);
    localStorage.setItem(key, JSON.stringify(progression));
  } catch (e) {
    console.error('[ParcoursProgressionStorage] Erreur ecriture localStorage:', e);
  }
}

function getDefaultProgression() {
  return {
    currentChapitre: 1,
    chapitre1: { completedSteps: {} }
  };
}

// ============================================================
// API publique
// ============================================================

/**
 * Charger la progression parcours.
 * @returns {Object} progression
 */
export function getParcoursProgression() {
  return loadFromLocalStorage();
}

/**
 * Sauvegarder la progression parcours.
 * @param {Object} progression
 */
export function saveParcoursProgression(progression) {
  saveToLocalStorage(progression);
  // Sync Firestore en arriere-plan
  syncToFirestore(progression);
}

// Extraire le numéro de chapitre depuis un stepId (ex: 'p1-11q' → 1, 'p15-3' → 15)
function getChapitreFromStepId(stepId) {
  if (!stepId) return null;
  const match = stepId.match(/^p(\d+)-/);
  return match ? parseInt(match[1]) : null;
}

/**
 * Marquer un step comme complete.
 * Pour les steps non-derivables : quiz, drill, memorization.
 * Pour les combo steps : utiliser subActivityId.
 *
 * @param {string} stepId - ex: 'p1-3', 'p1-7'
 * @param {string|null} subActivityId - ex: 'quiz', 'memo' (pour les combos)
 */
export function markStepComplete(stepId, subActivityId = null) {
  const progression = loadFromLocalStorage();
  // Utiliser le chapitre issu du stepId pour éviter la contamination inter-chapitres
  // (ex: un user au chapitre 15 qui complète p1-11q stocke dans chapitre1, pas chapitre15)
  const chapitreFromStep = getChapitreFromStepId(stepId);
  const chapitre = chapitreFromStep || progression.currentChapitre;
  const chapitreKey = `chapitre${chapitre}`;

  if (!progression[chapitreKey]) {
    progression[chapitreKey] = { completedSteps: {} };
  } else if (!progression[chapitreKey].completedSteps) {
    progression[chapitreKey].completedSteps = {};
  }

  if (subActivityId) {
    // Combo step : marquer la sous-activite
    const comboKey = `${stepId}-${subActivityId}`;
    progression[chapitreKey].completedSteps[comboKey] = true;
  } else {
    progression[chapitreKey].completedSteps[stepId] = true;
  }

  saveParcoursProgression(progression);
  // Notifier le ParcoursContext pour qu'il recalcule l'etat (evite l'affichage obsolete)
  emitProgressionChanged('step-complete', { stepId, subActivityId });
}

/**
 * Verifier si un step est complete dans le stockage parcours.
 * Ne concerne que les steps non-derivables.
 *
 * @param {string} stepId
 * @param {string|null} subActivityId
 * @returns {boolean}
 */
export function isStepCompleteInStorage(stepId, subActivityId = null) {
  const progression = loadFromLocalStorage();

  // Chercher dans tous les chapitres (pas seulement le chapitre courante).
  // Nécessaire pour afficher correctement les chapitres passées (ex: chapitre 1 vu depuis chapitre 3).
  const chapitreKeys = Object.keys(progression).filter(k => /^chapitre\d+$/.test(k));

  for (const chapitreKey of chapitreKeys) {
    const steps = progression[chapitreKey]?.completedSteps || {};

    if (subActivityId) {
      if (steps[`${stepId}-${subActivityId}`] === true) return true;
    } else {
      if (steps[stepId] === true) return true;

      // Fallback : détecter les anciennes clés mal formées (ex: 'p1-3-quiz' au lieu de 'p1-3').
      const suffixes = ['quiz', 'memo', 'drill', 'memorization', 'metronome', 'reproduire', 'breaks'];
      for (const suffix of suffixes) {
        if (steps[`${stepId}-${suffix}`] === true) {
          progression[chapitreKey].completedSteps[stepId] = true;
          saveToLocalStorage(progression);
          return true;
        }
      }
    }
  }

  return false;
}

/**
 * Obtenir le chapitre courante.
 * @returns {number}
 */
export function getCurrentChapitre() {
  const progression = loadFromLocalStorage();
  return progression.currentChapitre;
}

/**
 * Avancer a le chapitre suivante.
 * Refuse tout saut autre que +1 (garde-fou contre un chapitre aberrant propagé par la sync).
 * @param {number} nextChapitre
 * @param {string} [source] - origine de l'appel, pour le monitoring ('bouton', 'grosse-caisse'...)
 */
export function advanceToChapitre(nextChapitre, source = 'inconnue') {
  const progression = loadFromLocalStorage();
  const from = progression.currentChapitre;
  console.log('[ParcoursDebug][Storage] advanceToChapitre', { from, to: nextChapitre, source });
  if (!isValidChapitreAdvance(from, nextChapitre)) {
    MonitoringService.warning('parcours_chapitre_saut_refuse', { from, to: nextChapitre, source }, getCurrentUserId());
    return;
  }
  MonitoringService.info('parcours_chapitre_change', { from, to: nextChapitre, source }, getCurrentUserId());
  progression.currentChapitre = nextChapitre;
  saveParcoursProgression(progression);
  emitProgressionChanged('advance-chapitre', { nextChapitre });
}

// ============================================================
// Gestion des steps echoues (rattrapage en fin de parcours)
// ============================================================

/**
 * Marquer un step comme echoue (joue mais non valide).
 * Le step reste aussi dans completedSteps pour que la progression avance.
 */
export function markStepFailed(stepId, subActivityId = null) {
  const progression = loadFromLocalStorage();
  const chapitreFromStep = getChapitreFromStepId(stepId);
  const chapitre = chapitreFromStep || progression.currentChapitre;
  const chapitreKey = `chapitre${chapitre}`;

  if (!progression[chapitreKey]) progression[chapitreKey] = { completedSteps: {}, failedSteps: {} };
  if (!progression[chapitreKey].failedSteps) progression[chapitreKey].failedSteps = {};

  const key = subActivityId ? `${stepId}-${subActivityId}` : stepId;
  progression[chapitreKey].failedSteps[key] = true;

  console.log('[ParcoursDebug][Storage] markStepFailed', { stepId, subActivityId, chapitreKey });
  saveParcoursProgression(progression);
}

/**
 * Supprimer un step de la liste des echecs (apres retry, qu'il soit reussi ou non).
 * Cherche dans tous les chapitres pour couvrir les retries de chapitres precedentes.
 */
export function clearFailedStep(stepId, subActivityId = null) {
  const progression = loadFromLocalStorage();
  const key = subActivityId ? `${stepId}-${subActivityId}` : stepId;
  const chapitreKeys = Object.keys(progression).filter(k => /^chapitre\d+$/.test(k));

  let changed = false;
  for (const chapitreKey of chapitreKeys) {
    if (progression[chapitreKey]?.failedSteps?.[key]) {
      delete progression[chapitreKey].failedSteps[key];
      changed = true;
    }
  }

  console.log('[ParcoursDebug][Storage] clearFailedStep', { stepId, subActivityId, key, changed });
  if (changed) saveParcoursProgression(progression);
}

/**
 * Retourner les IDs de tous les steps echoues, toutes chapitres confondues.
 * Cherche dans tous les chapitres pour que l'orange persiste apres
 * avoir passe a le chapitre suivante.
 * @returns {string[]} ex: ['p1-3', 'p1-5-quiz']
 */
export function getFailedStepIds() {
  const progression = loadFromLocalStorage();
  const chapitreKeys = Object.keys(progression).filter(k => /^chapitre\d+$/.test(k));
  const allFailed = [];
  for (const chapitreKey of chapitreKeys) {
    const failed = Object.keys(progression[chapitreKey]?.failedSteps || {});
    allFailed.push(...failed);
  }
  return allFailed;
}

/**
 * Verifier s'il reste des steps echoues a rattraper.
 * @returns {boolean}
 */
export function hasFailedSteps() {
  return getFailedStepIds().length > 0;
}

// ============================================================
// Progression partielle (steps à sous-activités répétées, ex: série de breaks)
// ============================================================

/**
 * Sauvegarder l'avancement partiel d'un step composé de plusieurs sous-activités
 * jouées en séquence (ex: série de N breaks). Permet de reprendre au bon endroit
 * si l'élève quitte avant la fin de la série.
 *
 * @param {string} stepId - ex: 'p3-2'
 * @param {number} count - nombre de sous-activités validées jusqu'ici
 */
export function setStepPartialProgress(stepId, count) {
  const progression = loadFromLocalStorage();
  const chapitreFromStep = getChapitreFromStepId(stepId);
  const chapitre = chapitreFromStep || progression.currentChapitre;
  const chapitreKey = `chapitre${chapitre}`;

  if (!progression[chapitreKey]) progression[chapitreKey] = { completedSteps: {} };
  if (!progression[chapitreKey].partialProgress) progression[chapitreKey].partialProgress = {};

  progression[chapitreKey].partialProgress[stepId] = count;
  saveParcoursProgression(progression);
}

/**
 * Lire l'avancement partiel sauvegardé d'un step (0 si aucun).
 * @param {string} stepId
 * @returns {number}
 */
export function getStepPartialProgress(stepId) {
  const progression = loadFromLocalStorage();
  const chapitreKeys = Object.keys(progression).filter(k => /^chapitre\d+$/.test(k));

  for (const chapitreKey of chapitreKeys) {
    const count = progression[chapitreKey]?.partialProgress?.[stepId];
    if (count !== undefined) return count;
  }

  return 0;
}

/**
 * Effacer l'avancement partiel d'un step (une fois la série terminée, réussie ou non).
 * @param {string} stepId
 */
export function clearStepPartialProgress(stepId) {
  const progression = loadFromLocalStorage();
  const chapitreKeys = Object.keys(progression).filter(k => /^chapitre\d+$/.test(k));

  let changed = false;
  for (const chapitreKey of chapitreKeys) {
    if (progression[chapitreKey]?.partialProgress?.[stepId] !== undefined) {
      delete progression[chapitreKey].partialProgress[stepId];
      changed = true;
    }
  }

  if (changed) saveParcoursProgression(progression);
}

// ============================================================
// Tracking steps tentés (verrouillage séquentiel)
// ============================================================

/**
 * Marquer un step comme tenté (démarré, qu'il soit validé ou non).
 * Utilisé pour le verrouillage séquentiel : step N+1 accessible uniquement
 * si step N a été tenté (ou complété).
 *
 * @param {string} stepId - ex: 'p1-1', 'p1-3'
 */
export function markStepAttempted(stepId) {
  if (!stepId) return;
  const progression = loadFromLocalStorage();
  const chapitre = progression.currentChapitre;
  const chapitreKey = `chapitre${chapitre}`;

  if (!progression[chapitreKey]) progression[chapitreKey] = { completedSteps: {}, attemptedSteps: {} };
  if (!progression[chapitreKey].attemptedSteps) progression[chapitreKey].attemptedSteps = {};

  progression[chapitreKey].attemptedSteps[stepId] = true;
  saveParcoursProgression(progression);
}

/**
 * Vérifier si un step a été tenté (démarré).
 * Un step complété est aussi considéré comme tenté.
 *
 * @param {string} stepId
 * @returns {boolean}
 */
export function isStepAttempted(stepId) {
  if (!stepId) return false;
  const progression = loadFromLocalStorage();
  const chapitreKeys = Object.keys(progression).filter(k => /^chapitre\d+$/.test(k));

  for (const chapitreKey of chapitreKeys) {
    if (progression[chapitreKey]?.attemptedSteps?.[stepId] === true) return true;
    // Un step complété (même en échec) compte aussi comme tenté
    if (progression[chapitreKey]?.completedSteps?.[stepId] === true) return true;
  }

  return false;
}

// ============================================================
// Reinitialisation
// ============================================================

// ============================================================
// Tracking rythmes complétés dans le parcours (rhythm-group)
// ============================================================

/**
 * Marquer un rythme comme complété dans le contexte du parcours.
 * Indépendant de progressStorage (accès libre).
 *
 * @param {string} stepId - ex: 'p1-2'
 * @param {string} exerciseId - ex: 'exercise-id-1'
 */
export function markRhythmCompleteInParcours(stepId, exerciseId) {
  const progression = loadFromLocalStorage();
  const chapitre = progression.currentChapitre;
  const chapitreKey = `chapitre${chapitre}`;

  if (!progression[chapitreKey]) {
    progression[chapitreKey] = { completedSteps: {}, rhythmGroupProgress: {} };
  }
  if (!progression[chapitreKey].rhythmGroupProgress) {
    progression[chapitreKey].rhythmGroupProgress = {};
  }
  if (!progression[chapitreKey].rhythmGroupProgress[stepId]) {
    progression[chapitreKey].rhythmGroupProgress[stepId] = { completedRhythms: [] };
  }

  const completedRhythms = progression[chapitreKey].rhythmGroupProgress[stepId].completedRhythms;
  if (!completedRhythms.includes(exerciseId)) {
    completedRhythms.push(exerciseId);
  }

  saveParcoursProgression(progression);
  emitProgressionChanged('rhythm-group', { stepId, exerciseId });
}

/**
 * Vérifier si un rythme a été complété dans le contexte du parcours.
 *
 * @param {string} stepId
 * @param {string} exerciseId
 * @returns {boolean}
 */
export function isRhythmCompleteInParcours(stepId, exerciseId) {
  const progression = loadFromLocalStorage();

  // Chercher dans tous les chapitres (pas seulement le chapitre courante).
  const chapitreKeys = Object.keys(progression).filter(k => /^chapitre\d+$/.test(k));

  for (const chapitreKey of chapitreKeys) {
    const completedRhythms =
      progression[chapitreKey]?.rhythmGroupProgress?.[stepId]?.completedRhythms || [];
    if (completedRhythms.includes(exerciseId)) return true;
  }

  return false;
}

// ============================================================
// Initialisation selon le niveau initial (drumYears)
// ============================================================

/**
 * Initialiser le chapitre de départ du parcours selon les années de pratique.
 * A appeler UNE SEULE FOIS lors de la création du profil.
 *
 * Mapping :
 *   1 an  → Chapitre 1 (débutant)
 *   2 ans → Chapitre 2
 *   3 ans → Chapitre 4
 *   4 ans → Chapitre 4
 *   5+ ans → Chapitre 9
 *
 * @param {number|string} drumYears - Années de pratique (1, 2, 3, 4, 6)
 */
export async function initParcoursFromDrumYears(drumYears) {
  const years = parseInt(drumYears) || 1;

  const startChapitre = startChapitreFromDrumYears(years) || 1;

  if (startChapitre > 1) {
    // Écrire en localStorage d'abord
    const progression = loadFromLocalStorage();
    MonitoringService.info('parcours_chapitre_change', { from: progression.currentChapitre, to: startChapitre, source: 'init-drumyears' }, getCurrentUserId());
    progression.currentChapitre = startChapitre;
    saveToLocalStorage(progression);
    // Attendre que Firestore soit à jour avant que navigate('/') soit appelé
    await syncToFirestore(progression);
    emitProgressionChanged('advance-chapitre', { nextChapitre: startChapitre });
    console.log(`[Parcours] Chapitre initial défini à ${startChapitre} (drumYears: ${years})`);
  }
}

/**
 * Pré-débloquer le contenu (paliers Roulette, etc.) selon les années de pratique.
 * Lit la config Firestore du parcours pour savoir quels paliers sont dans les
 * chapitres sautés, puis marque ces paliers comme accessibles (flag preUnlocked)
 * SANS créer de faux rythmes joués.
 *
 * Idempotent : le flag contentPreUnlocked en localStorage évite de re-exécuter.
 * Fonctionne pour les nouveaux comptes ET les comptes existants.
 *
 * @param {number|string} drumYears - Années de pratique (1-5+)
 * @param {string} userId - ID de l'utilisateur Firebase
 */
// Mapping fixe chapitre de départ → nombre de paliers Roulette à pré-débloquer
// (indépendant de la config parcours pour être robuste)
const ROULETTE_PREUNLOCK_BY_CHAPITRE = {
  2: 2,
  4: 3,
  9: 8
};
const PREUNLOCK_VERSION = 2;

export async function preUnlockContentFromDrumYears(drumYears, userId) {
  const years = parseInt(drumYears) || 1;

  let startChapitre = 1;
  if (years >= 5) startChapitre = 9;
  else if (years >= 4) startChapitre = 4;
  else if (years >= 3) startChapitre = 4;
  else if (years >= 2) startChapitre = 2;

  // Vérifier si déjà fait avec la version actuelle pour ce niveau de départ
  const progression = loadFromLocalStorage();
  if (
    progression.contentPreUnlocked >= startChapitre &&
    progression.contentPreUnlockedVersion >= PREUNLOCK_VERSION
  ) return;

  if (startChapitre <= 1) {
    const prog = loadFromLocalStorage();
    prog.contentPreUnlocked = 1;
    prog.contentPreUnlockedVersion = PREUNLOCK_VERSION;
    saveToLocalStorage(prog);
    return;
  }

  try {
    const tiersToUnlock = ROULETTE_PREUNLOCK_BY_CHAPITRE[startChapitre] ?? 0;

    if (tiersToUnlock > 0 && userId) {
      // Pré-débloquer dans Firestore
      await RouletteProgressService.preUnlockTiersUpTo(userId, tiersToUnlock + 1);
      // Synchroniser aussi dans localStorage pour que le guard de RouletteExercisePage passe
      for (let i = 1; i <= tiersToUnlock; i++) {
        markTierParcoursUnlocked(i);
      }
      console.log(`[Parcours] Roulette pré-débloquée tiers 1-${tiersToUnlock} (chapitre ${startChapitre}, drumYears: ${years})`);
    }

    const prog = loadFromLocalStorage();
    prog.contentPreUnlocked = startChapitre;
    prog.contentPreUnlockedVersion = PREUNLOCK_VERSION;
    saveToLocalStorage(prog);
    syncToFirestore(prog);
  } catch (error) {
    console.error('[Parcours] Erreur pré-déblocage contenu:', error);
  }
}

/** Collecte les IDs de paliers Roulette dans un step (direct ou sous-activité combo). */
function _collectStepRouletteTiers(step, tierSet) {
  if (step.type === 'roulette') {
    const tier = step.params?.tier ?? step.tier;
    if (tier != null) tierSet.add(parseInt(tier));
  }
  if (step.type === 'combo') {
    const activities = step.params?.activities || step.activities || [];
    activities.forEach(act => {
      if (act?.type === 'roulette') {
        const tier = act.params?.tier ?? act.tier;
        if (tier != null) tierSet.add(parseInt(tier));
      }
    });
  }
}

// ============================================================
// Migration : déplacer les clés de complétion dans le bon chapitre
// ============================================================

/**
 * Déplace les clés de steps stockées dans le mauvais chapitre vers leur chapitre réel.
 *
 * Contexte : un bug dans markStepComplete utilisait currentChapitre au lieu du chapitre
 * issu du stepId. Un utilisateur au chapitre 15 qui complétait 'p1-11q' (chapitre 1)
 * stockait la clé dans chapitre15 au lieu de chapitre1.
 *
 * Idempotent : flag localStorage 'crossChapitreKeysMigrated_v1' évite la re-exécution.
 */
export function migrateCrossChapitreKeys() {
  const flagKey = 'crossChapitreKeysMigrated_v1';
  if (localStorage.getItem(flagKey)) return;

  const progression = loadFromLocalStorage();
  const chapitreKeys = Object.keys(progression).filter(k => /^chapitre\d+$/.test(k));

  let changed = false;
  for (const chapitreKey of chapitreKeys) {
    const storeNum = parseInt(chapitreKey.replace('chapitre', ''));
    const completedSteps = { ...(progression[chapitreKey]?.completedSteps || {}) };

    for (const key of Object.keys(completedSteps)) {
      const match = key.match(/^p(\d+)-/);
      if (!match) continue;
      const keyChapitreNum = parseInt(match[1]);

      if (keyChapitreNum !== storeNum) {
        const correctKey = `chapitre${keyChapitreNum}`;
        if (!progression[correctKey]) progression[correctKey] = { completedSteps: {} };
        if (!progression[correctKey].completedSteps) progression[correctKey].completedSteps = {};
        // Déplacer vers le bon chapitre (sans écraser si déjà présent)
        if (!progression[correctKey].completedSteps[key]) {
          progression[correctKey].completedSteps[key] = true;
        }
        delete progression[chapitreKey].completedSteps[key];
        changed = true;
      }
    }
  }

  if (changed) saveToLocalStorage(progression);
  localStorage.setItem(flagKey, '1');
}

// ============================================================
// Migration quiz-lecture (déploiement feature)
// ============================================================

/**
 * Marque automatiquement comme validés tous les steps quiz-lecture
 * dans les chapitres DÉJÀ PASSÉS (< currentChapitre).
 *
 * Contexte : les steps quiz-lecture ont été ajoutés rétroactivement.
 * Les élèves qui ont avancé au-delà d'un chapitre ne peuvent pas les avoir
 * échoués (ils n'existaient pas). Seuls les steps quiz-lecture sont touchés —
 * les exercices réellement échoués conservent leur indicateur "!".
 *
 * Idempotent : flag localStorage 'quizLectureMigrated' empêche la re-exécution.
 */
export function migrateQuizLectureForPastChapitres() {
  if (localStorage.getItem('quizLectureMigrated')) return;

  const progression = loadFromLocalStorage();
  const currentChapitre = progression.currentChapitre || 1;
  const chapitres = getChapitres();

  if (!chapitres || currentChapitre <= 1) {
    localStorage.setItem('quizLectureMigrated', '1');
    return;
  }

  let migrated = false;

  for (let n = 1; n < currentChapitre; n++) {
    const chapitreConfig = chapitres[n];
    if (!chapitreConfig?.steps) continue;

    const quizLectureSteps = chapitreConfig.steps.filter(s => s.type === 'quiz-lecture');
    if (quizLectureSteps.length === 0) continue;

    const chapitreKey = `chapitre${n}`;
    if (!progression[chapitreKey]) progression[chapitreKey] = { completedSteps: {} };
    if (!progression[chapitreKey].completedSteps) progression[chapitreKey].completedSteps = {};

    for (const step of quizLectureSteps) {
      if (!progression[chapitreKey].completedSteps[step.id]) {
        progression[chapitreKey].completedSteps[step.id] = true;
        migrated = true;
      }
    }
  }

  if (migrated) {
    saveParcoursProgression(progression);
    emitProgressionChanged('migration', { source: 'quizLectureMigration' });
  }

  localStorage.setItem('quizLectureMigrated', '1');
}

// ============================================================
// Reinitialisation
// ============================================================

/**
 * Reinitialiser toute la progression parcours.
 */
export function resetParcoursProgression() {
  const defaultProg = getDefaultProgression();
  saveToLocalStorage(defaultProg);
}

// ============================================================
// Sync Firestore
// ============================================================

async function syncToFirestore(progression) {
  const userId = getCurrentUserId();
  if (!userId) return;

  try {
    const docRef = doc(db, 'parcoursProgression', userId);
    await setDoc(docRef, {
      ...progression,
      userId,
      lastUpdated: new Date()
    }, { merge: true });
  } catch (e) {
    console.warn('[ParcoursProgressionStorage] Firestore sync echoue:', e.message);
    MonitoringService.error('parcours_step_save_failed', { errorMessage: e.message }, userId);
  }
}

/**
 * Charger et synchroniser la progression depuis Firestore.
 * Firestore a priorite si plus recent que localStorage.
 *
 * @returns {Object} progression synchronisee
 */
export async function loadAndSyncProgression() {
  const userId = getCurrentUserId();
  if (!userId) return loadFromLocalStorage();

  // Recale un chapitre courant très en avance sur l'activité réelle (cf. parcoursProgressionGuard).
  // Retourne true si la progression a été corrigée (mutation en place).
  // Chapitre de départ selon les années de batterie déclarées à l'inscription (null si inconnu)
  let startChapitre = null;
  try {
    const userSnap = await getDoc(doc(db, 'users', userId));
    startChapitre = startChapitreFromDrumYears(userSnap.data()?.profile?.drumYears);
  } catch (e) {
    console.warn('[ParcoursProgressionStorage] drumYears illisible:', e.message);
  }

  // Retourne les clés de chapitres supprimées (tableau, vide si rien à supprimer), ou null si saine.
  const repairIfOvershoot = (progression, source) => {
    const target = findChapitreOvershoot(progression, startChapitre);
    if (target === null) return null;
    const from = progression.currentChapitre;
    progression.currentChapitre = target;
    // Les chapitres au-delà du recalage repartent vierges (sinon la sync les recopierait)
    const removed = dropChapitresAfter(progression, target);
    MonitoringService.warning('parcours_chapitre_repare', { from, to: target, source, chapitresSupprimes: removed.length, drumYearsConnu: startChapitre !== null }, userId);
    return removed;
  };

  // Firestore : setDoc(merge) ne retire pas de clés, il faut les supprimer explicitement
  const deleteFirestoreChapitres = async (removed) => {
    if (!removed || removed.length === 0) return;
    const updates = {};
    removed.forEach(k => { updates[k] = deleteField(); });
    await updateDoc(doc(db, 'parcoursProgression', userId), updates);
  };

  try {
    const docRef = doc(db, 'parcoursProgression', userId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const firestoreData = migratePhaseKeysToChapitre(docSnap.data());
      const localData = loadFromLocalStorage();

      const firestoreRemoved = repairIfOvershoot(firestoreData, 'firestore');
      const localRemoved = repairIfOvershoot(localData, 'local');
      const firestoreRepaired = firestoreRemoved !== null;
      const localRepaired = localRemoved !== null;

      // Surveillance (sans réparation) : élèves sans drumYears, dont le seuil de réparation est large (10).
      // On signale tout chapitre entièrement vide pour repérer les sauts qui passent sous le seuil.
      if (startChapitre === null) {
        [['firestore', firestoreData, firestoreRepaired], ['local', localData, localRepaired]].forEach(([source, data, repaired]) => {
          if (repaired) return;
          const trou = findChapitreOvershoot(data, null, 1);
          if (trou !== null) {
            MonitoringService.warning('parcours_trou_sans_drumyears', { source, chapitre: data.currentChapitre, dernierActif: trou }, userId);
          }
        });
      }

      if (firestoreData.currentChapitre !== localData.currentChapitre) {
        MonitoringService.info('parcours_sync_chapitres_differents', {
          local: localData.currentChapitre,
          firestore: firestoreData.currentChapitre
        }, userId);
      }

      // Prendre la version avec le chapitre la plus avancee
      // ou le plus de steps completes si meme chapitre
      const useFirestore = shouldPreferFirestore(localData, firestoreData);
      const chosen = useFirestore ? firestoreData : localData;

      if (useFirestore) saveToLocalStorage(firestoreData);
      // Sync vers Firestore si le local gagne, ou si une réparation vient d'avoir lieu
      if (!useFirestore || firestoreRepaired || localRepaired) {
        if (localRepaired || firestoreRepaired) saveToLocalStorage(chosen);
        // Suppression côté Firestore uniquement pour les chapitres retirés du document Firestore lui-même
        syncToFirestore(chosen).then(() => deleteFirestoreChapitres(firestoreRemoved)).catch(() => {});
      }
      return chosen;
    } else {
      // Pas de donnees Firestore, sync local vers Firestore
      const localData = loadFromLocalStorage();
      if (repairIfOvershoot(localData, 'local') !== null) saveToLocalStorage(localData);
      syncToFirestore(localData);
      return localData;
    }
  } catch (e) {
    console.warn('[ParcoursProgressionStorage] Firestore load echoue:', e.message);
    return loadFromLocalStorage();
  }
}

function shouldPreferFirestore(local, firestore) {
  // Chapitre plus avancee = priorite
  if ((firestore.currentChapitre || 1) > (local.currentChapitre || 1)) return true;
  if ((firestore.currentChapitre || 1) < (local.currentChapitre || 1)) return false;

  // Meme chapitre : comparer le nombre de steps completes
  const chapitre = local.currentChapitre || 1;
  const chapitreKey = `chapitre${chapitre}`;

  const localSteps = Object.keys(local[chapitreKey]?.completedSteps || {}).length;
  const firestoreSteps = Object.keys(firestore[chapitreKey]?.completedSteps || {}).length;

  return firestoreSteps > localSteps;
}
